import { getPlacementRuleLabel } from "./placementRules";
import { formatTournamentHandicap } from "./tournamentScoringType";

const money = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);

const formatFeeBreakout = (fees) =>
  [...(fees || [])]
    .sort((a, b) => {
      if (a.feeName === "Prize Fund") return -1;
      if (b.feeName === "Prize Fund") return 1;
      return (a.feeName || "").localeCompare(b.feeName || "");
    })
    .filter((fee) => fee.amount != null && Number(fee.amount) > 0)
    .map((fee) => `${fee.feeName} $${Number(fee.amount).toFixed(2)}`)
    .join(" · ");

export const EVENT_RULES_HEADING = "Event rules";
export const EVENT_RULES_EMPTY = "No event rules posted yet";

export const processEventRulesRow = (event) => {
  const fees = (event.eventFee || event.fees || []).map((row) => ({
    feeName: row.fees?.feeName || row.feeName,
    amount: row.amount,
  }));
  const perPersonCost = fees.reduce((sum, fee) => sum + (fee.amount || 0), 0);
  const bowlerQuantity = Number(event.bowlerQuantity) || 1;

  return {
    ...event,
    fees,
    perPersonCost,
    perTeamCost: perPersonCost * bowlerQuantity,
    calculationTypeName:
      event.calculationTypeName ||
      event.tournamentEventCalculationType?.name ||
      null,
    calculationTypeCode:
      event.calculationTypeCode ||
      event.tournamentEventCalculationType?.code ||
      null,
  };
};

export const formatPayoutRatioLine = (tournament) => {
  const numerator = Number(tournament?.payoutRatioNumerator);
  const denominator = Number(tournament?.payoutRatioDenominator);
  if (!numerator || !denominator) return "";
  return `Pays ${numerator} in ${denominator}`;
};

export const formatEventFormatLine = (event) => {
  const parts = [];
  const quantity = Number(event?.bowlerQuantity) || 0;
  if (quantity > 2) {
    parts.push(`${quantity} bowlers`);
  }
  if (typeof event?.gamesPerEvent === "number" && event.gamesPerEvent > 0) {
    parts.push(
      event.gamesPerEvent === 1 ? "1 game" : `${event.gamesPerEvent} games`
    );
  }
  return parts.join(" · ");
};

export const formatEventFeeLine = (event) => {
  if (!(event?.perPersonCost > 0)) return "";
  if (Number(event.bowlerQuantity) > 1) {
    return `${money(event.perPersonCost)} per person · ${money(
      event.perTeamCost
    )} team`;
  }
  return money(event.perPersonCost);
};

export const formatEventMoney = (event) => {
  if (!(event?.perTeamCost > 0)) return "";
  return money(event.perTeamCost);
};

export const formatFlyerMoney = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const sameLabel = (left, right) =>
  String(left || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase() ===
  String(right || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const isAllEventRow = (event) => {
  const code =
    event?.calculationTypeCode ||
    event?.tournamentEventCalculationType?.code ||
    "";
  if (code === "allEventHdcp" || code === "allEventScratch") return true;
  return /all\s*event/i.test(event?.eventName || "");
};

export const eventMenuRow = (event) => ({
  id: event.id,
  name: event.eventName,
  detail: event.isPrimary ? formatEventFormatLine(event) : "",
  price: formatFlyerMoney(event.perTeamCost),
});

export const bracketMenuRow = (product) => ({
  id: product.id,
  name: product.name || "Brackets",
  detail: "",
  price: formatFlyerMoney(product.entryAmount),
});

const sharedGroupPrice = (items) => {
  if (!items || items.length < 2) return "";
  const prices = items.map((item) => item.price);
  if (!prices[0] || prices.some((price) => price !== prices[0])) return "";
  return prices[0];
};

const withSharedPrice = (group) => {
  if (group.key === "primary") return { ...group, sharedPrice: "" };
  return {
    ...group,
    sharedPrice: sharedGroupPrice(group.items),
  };
};

export const overviewEventMenu = ({ events = [], brackets = [] } = {}) => {
  const primary = [];
  const sidePots = [];
  const allEvents = [];

  (events || []).forEach((event) => {
    if (event.isPrimary) {
      primary.push(eventMenuRow(event));
      return;
    }
    if (isAllEventRow(event)) {
      allEvents.push(eventMenuRow(event));
      return;
    }
    sidePots.push(eventMenuRow(event));
  });

  return [
    { key: "primary", title: "", items: primary },
    { key: "sidePots", title: "Side pots", items: sidePots },
    {
      key: "brackets",
      title: "Brackets",
      items: (brackets || []).map(bracketMenuRow),
    },
    { key: "allEvents", title: "All events", items: allEvents },
  ]
    .filter((group) => group.items.length)
    .map(withSharedPrice);
};

export const tournamentRulesIntroLines = (tournament) => {
  const scoring = formatTournamentHandicap(tournament);
  const lines = [];
  if (scoring && scoring !== "Not set") lines.push(scoring);
  const payout = formatPayoutRatioLine(tournament);
  if (payout) lines.push(payout);
  return lines;
};

export const tournamentRulesIntro = (tournament) =>
  tournamentRulesIntroLines(tournament).join(". ");

export const eventRulesCopy = (event) => {
  const calc = event.calculationTypeName;
  const calcIfDifferent =
    calc && !sameLabel(calc, event.eventName) ? calc : "";
  const format = [
    formatEventFormatLine(event),
    calcIfDifferent,
    event.isTemplate ? "Per squad" : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    name: event.eventName,
    format,
    placement: getPlacementRuleLabel(
      event.bowlerQuantity,
      event.maxSharedPlayers
    ),
    entry: formatEventFeeLine(event),
    money: formatEventMoney(event),
    fees: formatFeeBreakout(event.fees),
  };
};

const allEqual = (values) => {
  const list = (values || []).filter((value) => value);
  if (list.length < 2) return "";
  return list.every((value) => value === list[0]) ? list[0] : "";
};

const isPrizeFundOnly = (fees) =>
  /^Prize Fund \$[\d,.]+$/.test(String(fees || "").trim());

const eventRulesItem = (event) => ({
  id: event.id,
  isPrimary: Boolean(event.isPrimary),
  isAllEvent: isAllEventRow(event),
  amount: event.perTeamCost,
  ...eventRulesCopy(event),
});

const liftSharedFields = (group) => {
  const extras = group.key !== "primary";
  const sharedPlacement = allEqual(group.items.map((item) => item.placement));
  const sharedFees = allEqual(group.items.map((item) => item.fees));
  const sharedMoney = extras
    ? allEqual(group.items.map((item) => item.money))
    : "";
  let liftedFees = sharedFees;
  let sharedEach = "";
  if (extras && sharedMoney) {
    const price = formatFlyerMoney(group.items[0].amount);
    if (!sharedFees || isPrizeFundOnly(sharedFees)) {
      sharedEach = price ? `${price} each` : "";
      liftedFees = "";
    }
  }

  return {
    ...group,
    sharedPlacement,
    sharedFees: liftedFees,
    sharedEach,
    items: group.items.map((item) => ({
      ...item,
      placement: sharedPlacement ? "" : item.placement,
      fees: sharedFees ? "" : item.fees,
      money: sharedMoney ? "" : item.money,
    })),
  };
};

export const buildEventRulesPage = ({
  events = [],
  brackets = [],
  tournament,
  afterDoubles = "",
  laneLines = [],
} = {}) => {
  const bowling = (events || []).map(eventRulesItem);
  const pagePlacement = allEqual(bowling.map((row) => row.placement));
  const pageFees = allEqual(bowling.map((row) => row.fees));
  const afterPage = bowling.map((row) => ({
    ...row,
    placement: pagePlacement ? "" : row.placement,
    fees: pageFees ? "" : row.fees,
  }));

  const primary = [];
  const sidePots = [];
  const allEvents = [];
  afterPage.forEach((row) => {
    if (row.isPrimary) primary.push(row);
    else if (row.isAllEvent) allEvents.push(row);
    else sidePots.push(row);
  });

  const groups = [
    { key: "primary", title: "", items: primary },
    { key: "sidePots", title: "Side pots", items: sidePots },
    {
      key: "brackets",
      title: "Brackets",
      items: (brackets || []).map((product) => ({
        id: product.id,
        name: product.name || "Brackets",
        format: "",
        placement: "",
        money: formatFlyerMoney(product.entryAmount),
        amount: Number(product.entryAmount) || 0,
        fees: "",
      })),
    },
    { key: "allEvents", title: "All events", items: allEvents },
  ]
    .filter((group) => group.items.length)
    .map(liftSharedFields);

  const introLines = [
    ...tournamentRulesIntroLines(tournament),
    afterDoubles,
    ...(laneLines || []),
    pagePlacement,
    pageFees,
  ].filter(Boolean);

  return { intro: introLines.join(". "), introLines, groups };
};

export const hasPostedEventRules = (events) => (events || []).length > 0;
